import { useState } from "react";
import { useAuth, API } from "../App";
import Layout from "../components/Layout";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  FileText,
  BarChart3,
  TrendingUp,
  Package,
  Download,
  Loader2,
  Calendar,
  MapPin,
  Shield,
  AlertTriangle
} from "lucide-react";

const COMMUNES = [
  "Abobo", "Adjamé", "Attécoubé", "Cocody", "Koumassi", 
  "Marcory", "Plateau", "Port-Bouët", "Treichville", "Yopougon"
];

const ReportsPage = () => {
  const { token, user } = useAuth();
  const [loading, setLoading] = useState(null);
  const [reportData, setReportData] = useState(null);
  const [selectedCommune, setSelectedCommune] = useState("all");

  const generateReport = async (reportType) => {
    setLoading(reportType);
    setReportData(null);
    
    try {
      const response = await axios.post(`${API}/reports/generate`, {
        report_type: reportType,
        commune: reportType === 'comparative' && selectedCommune !== 'all' ? selectedCommune : undefined
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setReportData({ type: reportType, ...response.data });
      toast.success("Rapport généré avec succès!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la génération");
    } finally {
      setLoading(null);
    }
  };

  const downloadReportJSON = () => {
    if (!reportData) return;
    
    const blob = new Blob([JSON.stringify(reportData, null, 2)], { type: 'application/json' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `rapport_${reportData.type}_${new Date().toISOString().split('T')[0]}.json`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('fr-CI', { 
      style: 'currency', 
      currency: 'XOF',
      maximumFractionDigits: 0 
    }).format(value);
  };

  const reportTypes = [
    {
      id: "comparative",
      title: "Rapport Comparatif",
      description: "Analyse anonyme par commune pour benchmarking",
      icon: BarChart3,
      color: "sky",
      features: ["Comparaison inter-pharmacies", "Données anonymisées", "Tendances par région"]
    },
    {
      id: "forecast",
      title: "Prévision Commande",
      description: "Recommandations de réapprovisionnement par catégorie",
      icon: TrendingUp,
      color: "emerald",
      features: ["Analyse des stocks", "Prédiction des besoins", "Alertes urgentes"]
    },
    {
      id: "unsold",
      title: "Gestion Invendus",
      description: "Produits à risque d'expiration prochaine",
      icon: Package,
      color: "amber",
      features: ["Alertes péremption", "Niveaux de risque", "Actions recommandées"]
    }
  ];

  return (
    <Layout>
      <div className="p-6 md:p-8 space-y-8" data-testid="reports-page">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-outfit font-bold text-white">
            Rapports & Analyses
          </h1>
          <p className="text-slate-400 mt-1">
            Générez des rapports détaillés pour optimiser votre gestion
          </p>
        </div>

        {/* Report Types Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {reportTypes.map((report) => {
            const Icon = report.icon;
            const isLoading = loading === report.id;
            const colorClasses = {
              sky: "border-sky-500/30 hover:border-sky-500/50",
              emerald: "border-emerald-500/30 hover:border-emerald-500/50",
              amber: "border-amber-500/30 hover:border-amber-500/50"
            };
            const iconColors = {
              sky: "bg-sky-500/10 text-sky-400",
              emerald: "bg-emerald-500/10 text-emerald-400",
              amber: "bg-amber-500/100/10 text-amber-400"
            };
            const buttonColors = {
              sky: "btn-primary",
              emerald: "bg-emerald-600 hover:bg-emerald-500 text-white",
              amber: "btn-destruction"
            };
            
            return (
              <Card 
                key={report.id}
                className={`glass-card transition-all duration-300 ${colorClasses[report.color]}`}
              >
                <CardHeader>
                  <div className={`p-3 rounded-xl ${iconColors[report.color]} w-fit mb-3`}>
                    <Icon className="w-6 h-6" />
                  </div>
                  <CardTitle className="text-white font-outfit">{report.title}</CardTitle>
                  <CardDescription className="text-slate-400">
                    {report.description}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2 mb-6">
                    {report.features.map((feature, idx) => (
                      <li key={idx} className="flex items-center gap-2 text-sm text-slate-300">
                        <div className="w-1.5 h-1.5 rounded-full bg-white/50" />
                        {feature}
                      </li>
                    ))}
                  </ul>

                  {/* Commune selector for comparative report */}
                  {report.id === 'comparative' && (
                    <div className="mb-4">
                      <Select value={selectedCommune} onValueChange={setSelectedCommune}>
                        <SelectTrigger className="bg-slate-950/50 border-white/10 text-white">
                          <MapPin className="w-4 h-4 mr-2 text-slate-500" />
                          <SelectValue placeholder="Filtrer par commune (optionnel)" />
                        </SelectTrigger>
                        <SelectContent className="bg-slate-900 border-white/10">
                          <SelectItem value="all" className="text-slate-300">Toutes les communes</SelectItem>
                          {COMMUNES.map((commune) => (
                            <SelectItem key={commune} value={commune} className="text-slate-300">
                              {commune}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  <Button
                    onClick={() => generateReport(report.id)}
                    disabled={isLoading}
                    data-testid={`generate-${report.id}-btn`}
                    className={`w-full ${buttonColors[report.color]}`}
                  >
                    {isLoading ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <>
                        <FileText className="w-4 h-4 mr-2" />
                        Générer
                      </>
                    )}
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Report Results */}
        {reportData && (
          <Card className="glass-card border-sky-500/20">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-white font-outfit flex items-center gap-2">
                  <FileText className="w-5 h-5 text-sky-400" />
                  Résultats du Rapport
                </CardTitle>
                <CardDescription className="text-slate-400">
                  Généré le {new Date(reportData.generated_at).toLocaleString('fr-FR')}
                </CardDescription>
              </div>
              <Button
                variant="outline"
                onClick={downloadReportJSON}
                data-testid="download-report-btn"
                className="border-sky-500/30 text-sky-400 hover:bg-sky-500/10 gap-2"
              >
                <Download className="w-4 h-4" />
                Télécharger JSON
              </Button>
            </CardHeader>
            <CardContent>
              {/* Comparative Report */}
              {reportData.type === 'comparative' && (
                <div className="space-y-4">
                  {reportData.metadata?.anonymized && (
                    <div className="flex items-center gap-2 p-3 rounded-lg bg-sky-500/10 border border-sky-500/20">
                      <Shield className="w-5 h-5 text-sky-400" />
                      <span className="text-sky-400 text-sm">
                        Données anonymisées pour respecter la confidentialité
                      </span>
                    </div>
                  )}
                  
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-white/10">
                          <th className="text-left p-3 text-slate-400">Commune</th>
                          <th className="text-left p-3 text-slate-400">Ref. Anonyme</th>
                          <th className="text-right p-3 text-slate-400">Destructions</th>
                          <th className="text-right p-3 text-slate-400">Valeur Totale</th>
                          <th className="text-right p-3 text-slate-400">Moy. par Destruction</th>
                        </tr>
                      </thead>
                      <tbody>
                        {reportData.data?.map((row, idx) => (
                          <tr key={idx} className="border-b border-white/5 hover:bg-white/5">
                            <td className="p-3 text-white">{row.commune}</td>
                            <td className="p-3">
                              <span className="text-slate-500 font-mono text-sm">
                                #{row.pharmacy_ref}
                              </span>
                            </td>
                            <td className="p-3 text-right text-white">{row.total_destructions}</td>
                            <td className="p-3 text-right text-amber-400">
                              {formatCurrency(row.total_value)}
                            </td>
                            <td className="p-3 text-right text-sky-400">
                              {formatCurrency(row.avg_value)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Forecast Report */}
              {reportData.type === 'forecast' && (
                <div className="space-y-4">
                  {reportData.data?.map((cat, idx) => (
                    <div 
                      key={idx}
                      className="flex items-center justify-between p-4 rounded-lg bg-slate-950/50 border border-white/5"
                    >
                      <div className="flex items-center gap-4">
                        <div className={`p-2 rounded-lg ${
                          cat.urgency === 'high' ? 'bg-red-500/100/10' : 'bg-emerald-500/10'
                        }`}>
                          <Package className={`w-5 h-5 ${
                            cat.urgency === 'high' ? 'text-red-400' : 'text-emerald-400'
                          }`} />
                        </div>
                        <div>
                          <p className="text-white font-medium capitalize">{cat.category}</p>
                          <p className="text-slate-500 text-sm">
                            {cat.products_count} produit(s) • Stock: {cat.current_stock}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <Badge className={cat.urgency === 'high' 
                          ? 'bg-red-500/100/20 text-red-400 border-red-500/30'
                          : 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30'
                        }>
                          {cat.urgency === 'high' ? 'Commander' : 'Stock OK'}
                        </Badge>
                        {cat.recommended_order > 0 && (
                          <p className="text-amber-400 text-sm mt-1">
                            +{cat.recommended_order} recommandés
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Unsold Report */}
              {reportData.type === 'unsold' && (
                <div className="space-y-4">
                  {/* Summary */}
                  <div className="grid grid-cols-3 gap-4 mb-6">
                    <div className="p-4 rounded-lg bg-red-500/100/10 border border-red-500/20 text-center">
                      <p className="text-3xl font-bold text-red-400">{reportData.summary?.critical || 0}</p>
                      <p className="text-slate-400 text-sm">Critique (&lt;30j)</p>
                    </div>
                    <div className="p-4 rounded-lg bg-amber-500/100/10 border border-amber-500/20 text-center">
                      <p className="text-3xl font-bold text-amber-400">{reportData.summary?.warning || 0}</p>
                      <p className="text-slate-400 text-sm">Attention (30-60j)</p>
                    </div>
                    <div className="p-4 rounded-lg bg-emerald-500/10 border border-emerald-500/20 text-center">
                      <p className="text-3xl font-bold text-emerald-400">{reportData.summary?.low || 0}</p>
                      <p className="text-slate-400 text-sm">Faible (60-90j)</p>
                    </div>
                  </div>

                  {reportData.data?.length > 0 ? (
                    <div className="space-y-2 max-h-80 overflow-y-auto">
                      {reportData.data.map((product, idx) => (
                        <div 
                          key={idx}
                          className="flex items-center justify-between p-3 rounded-lg bg-slate-950/50 border border-white/5"
                        >
                          <div className="flex items-center gap-3">
                            <div className={`w-2 h-2 rounded-full ${
                              product.risk_level === 'critical' ? 'bg-red-500/100' :
                              product.risk_level === 'warning' ? 'bg-amber-500/100' : 'bg-emerald-500'
                            }`} />
                            <div>
                              <p className="text-white">{product.name}</p>
                              <p className="text-slate-500 text-sm">Lot: {product.batch_number}</p>
                            </div>
                          </div>
                          <div className="text-right">
                            <Badge variant="outline" className={
                              product.risk_level === 'critical' ? 'border-red-500/30 text-red-400' :
                              product.risk_level === 'warning' ? 'border-amber-500/30 text-amber-400' :
                              'border-emerald-500/30 text-emerald-400'
                            }>
                              {product.days_to_expiry}j
                            </Badge>
                            <p className="text-slate-500 text-xs mt-1">
                              Exp: {product.expiry_date}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <Package className="w-12 h-12 text-emerald-400 mx-auto mb-3" />
                      <p className="text-emerald-400">Aucun produit à risque!</p>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
};

export default ReportsPage;
